import { useEffect, useRef, useState } from 'react';
import { data } from '../bridge/client';
import type { StoreTable } from '../data/db/database';

/**
 * Store-observing state: runs `read` now, and again whenever one of `tables` changes — the web
 * twin of a view model observing StoreChangeNotifier. Re-reads are coalesced to one per frame
 * (the "≤1 reload per ~150 ms, last write wins" rule — a plain throttle would drop the final
 * one), and an answer that arrives after a newer request was made is discarded, so a slow read
 * can never paint over a newer one.
 */
export function useLive<T>(
  read: () => Promise<T>,
  tables: StoreTable[],
  deps: unknown[],
  options: { initial?: () => T | undefined; first?: () => Promise<T> | null } = {},
): { value: T | undefined; loadedFor: string } {
  const [state, setState] = useState<{ value: T | undefined; loadedFor: string }>(() => {
    const initial = options.initial?.();
    return { value: initial, loadedFor: initial === undefined ? '' : 'cache' };
  });
  const generation = useRef(0);
  const key = JSON.stringify(deps);
  useEffect(() => {
    let disposed = false;
    let scheduled = false;
    let firstRun = true;
    const run = () => {
      scheduled = false;
      const mine = ++generation.current;
      const pending = (firstRun && options.first?.()) || read();
      firstRun = false;
      pending.then(
        (value) => {
          if (!disposed && mine === generation.current) setState({ value, loadedFor: key });
        },
        () => undefined,
      );
    };
    run();
    const watched = new Set(tables);
    const off = data.onChanges((changed) => {
      for (const t of changed) {
        if (watched.has(t)) {
          if (!scheduled) {
            scheduled = true;
            requestAnimationFrame(run);
          }
          return;
        }
      }
    });
    return () => {
      disposed = true;
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

/** A clock that ticks once a minute — relative times ("hace 5 min", "ayer") stay honest. */
export function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function useLocalStorage<T extends string>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      return (localStorage.getItem(key) as T | null) ?? initial;
    } catch {
      return initial;
    }
  });
  const set = (v: T) => {
    setValue(v);
    try {
      localStorage.setItem(key, v);
    } catch {
      /* a per-viewer convenience; losing it costs nothing */
    }
  };
  return [value, set];
}
